import React, { useState, useEffect } from 'react';
import './UnifiedEventInput.css';
import { getTagsForEventType } from '../../../utils/tagUtils'

/**
 * Unified component for event selection, outcome tags, and comments
 */
const UnifiedEventInput = (props) => {
  const {
    eventType,
    setEventType,
    team,
    setTeam,
    homeTeamName,
    awayTeamName,
    setHomeTeamName,
    setAwayTeamName,
    teamDirection,
    setTeamDirection,
    comment,
    setComment,
    EVENT_TYPES,
    selectedTags,
    setSelectedTags,
    showToast
  } = props;

  // Only use internal state if props for selected tags are not provided
  const [internalSelectedTags, setInternalSelectedTags] = useState([]);
  
  // Use either the passed in tags state or our internal state
  const effectiveTags = selectedTags || internalSelectedTags;
  const setEffectiveTags = setSelectedTags || setInternalSelectedTags;
  
  // States for editing team names
  const [editingHomeTeam, setEditingHomeTeam] = useState(false);
  const [editingAwayTeam, setEditingAwayTeam] = useState(false);
  const [tempHomeTeamName, setTempHomeTeamName] = useState(homeTeamName);
  const [tempAwayTeamName, setTempAwayTeamName] = useState(awayTeamName);

  // Reset tags when event type changes
  useEffect(() => {
    setEffectiveTags([]);
  }, [eventType, setEffectiveTags]);

  // Handle tag click
  const handleTagClick = (tagId) => {
    setEffectiveTags(prevTags => {
      const isSelected = prevTags.includes(tagId);
      if (isSelected) {
        // Remove tag if already selected
        return prevTags.filter(id => id !== tagId);
      } else {
        // Add tag if not selected
        return [...prevTags, tagId];
      }
    });
  };

  // Update comment with selected tags
  useEffect(() => {
    // Format selected tags as text
    const getTagLabel = (tagId) => {
      const allTags = getTagsForEventType(eventType);
      const tag = allTags.find(tag => tag.id === tagId);
      return tag ? tag.label : tagId;
    };

    // Create a string from selected tags
    const tagText = effectiveTags.map(getTagLabel).join(', ');
    
    // Set the comment with tags
    if (tagText) {
      // Remove any existing tags in brackets if present
      const existingComment = comment.replace(/\[.*?\]/, '').trim();
      setComment(existingComment ? `[${tagText}] ${existingComment}` : `[${tagText}]`);
    } else if (effectiveTags.length === 0 && comment.startsWith('[')) {
      // Remove tag formatting if all tags were deselected
      const existingComment = comment.replace(/\[.*?\]/, '').trim();
      setComment(existingComment);
    }
  }, [effectiveTags, setComment, comment, eventType]);

  // Team name editing functions
  const startEditingHomeTeam = () => {
    setTempHomeTeamName(homeTeamName);
    setEditingHomeTeam(true);
  };

  const startEditingAwayTeam = () => {
    setTempAwayTeamName(awayTeamName);
    setEditingAwayTeam(true);
  };

  const saveHomeTeamName = (e) => {
    e.preventDefault();
    if (tempHomeTeamName.trim()) {
      setHomeTeamName(tempHomeTeamName);
      setEditingHomeTeam(false);
    }
  };

  const saveAwayTeamName = (e) => {
    e.preventDefault();
    if (tempAwayTeamName.trim()) {
      setAwayTeamName(tempAwayTeamName);
      setEditingAwayTeam(false);
    }
  };

  // Function to handle direction change with confirmation
  const handleDirectionChange = () => {
    const newDirection = teamDirection === 'left-to-right' ? 'right-to-left' : 'left-to-right';
    
    if (window.confirm(`Change playing direction to ${newDirection === 'left-to-right' ? 'Left to Right' : 'Right to Left'}?`)) {
      setTeamDirection(newDirection);
      if (showToast) {
        showToast(`Changed playing direction to ${newDirection === 'left-to-right' ? 'Left to Right' : 'Right to Left'}`, 'info');
      }
    }
  };

  // Helper function to render tag buttons
  const renderTags = () => {
    const tags = getTagsForEventType(eventType);
    
    if (tags.length === 0) {
      return <div className="no-tags-message">No outcome options for this event type</div>;
    }
    
    return (
      <div className="event-tags-container">
        {tags.map(tag => (
          <button
            key={tag.id}
            className={`tag-button ${effectiveTags.includes(tag.id) ? 'selected' : ''}`}
            onClick={() => handleTagClick(tag.id)}
            type="button"
          >
            {tag.label}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="unified-event-input">
      <div className="section team-section">
        <h3 className="section-title">Team</h3>
        <div className="team-options">
          <div className="team-row">
            <label className="option-label">
              <input
                type="radio"
                name="team"
                value="Home"
                checked={team === "Home"}
                onChange={(e) => setTeam(e.target.value)}
              />
              {editingHomeTeam ? (
                <div className="team-name-edit">
                  <input
                    type="text"
                    value={tempHomeTeamName}
                    onChange={(e) => setTempHomeTeamName(e.target.value)}
                    className="team-name-input"
                  />
                  <button className="save-name-btn" onClick={saveHomeTeamName}>✓</button>
                </div>
              ) : (
                <span className="editable-name" onClick={startEditingHomeTeam}>
                  {homeTeamName} <span className="edit-icon">✎</span>
                </span>
              )}
            </label>
            <div
              className="direction-arrow"
              onClick={handleDirectionChange}
              title={`${homeTeamName} playing direction (click to change)`}
            >
              {teamDirection === 'left-to-right' ? '→' : '←'}
            </div>
          </div>
          
          <div className="team-row">
            <label className="option-label">
              <input
                type="radio"
                name="team"
                value="Away"
                checked={team === "Away"}
                onChange={(e) => setTeam(e.target.value)}
              />
              {editingAwayTeam ? (
                <div className="team-name-edit">
                  <input
                    type="text"
                    value={tempAwayTeamName}
                    onChange={(e) => setTempAwayTeamName(e.target.value)}
                    className="team-name-input"
                  />
                  <button className="save-name-btn" onClick={saveAwayTeamName}>✓</button>
                </div>
              ) : (
                <span className="editable-name" onClick={startEditingAwayTeam}>
                  {awayTeamName} <span className="edit-icon">✎</span>
                </span>
              )}
            </label>
            <div
              className="direction-arrow"
              onClick={handleDirectionChange}
              title={`${awayTeamName} playing direction (click to change)`}
            >
              {teamDirection === 'left-to-right' ? '←' : '→'}
            </div>
          </div>
        </div>
      </div>
      
      <div className="section event-section">
        <h3 className="section-title">Event Type</h3>
        <div className="event-options">
          <div className="event-column">
            {EVENT_TYPES.slice(0, Math.ceil(EVENT_TYPES.length / 2)).map(type => (
              <label key={type} className="option-label">
                <input
                  type="radio"
                  name="eventType"
                  value={type}
                  checked={eventType === type}
                  onChange={(e) => setEventType(e.target.value)}
                />
                <span>{type}</span>
              </label>
            ))}
          </div>
          <div className="event-column">
            {EVENT_TYPES.slice(Math.ceil(EVENT_TYPES.length / 2)).map(type => (
              <label key={type} className="option-label">
                <input
                  type="radio"
                  name="eventType"
                  value={type}
                  checked={eventType === type}
                  onChange={(e) => setEventType(e.target.value)}
                />
                <span>{type}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      
      <div className="section outcome-section">
        <h3 className="section-title">Outcome</h3>
        {renderTags()}
      </div>
      
      <div className="section comment-section">
        <h3 className="section-title">Comment</h3>
        <input
          type="text"
          className="comment-input"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add optional details about the event"
        />
      </div>
      
      {/* Event will be recorded by clicking on the field */}
    </div>
  );
};

export default UnifiedEventInput;